import React, { useState } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import Menu from '../../../atoms/Icon/Menu';
import Tooltip from '../../../atoms/Tooltip/Tooltip';
import AgenticWelcomeHeader from './AgenticWelcomeHeader';
import AgenticSearchInput from '../SearchBar/AgenticSearchInput';
import AgenticQuickActions from './AgenticQuickActions';
import AgenticQuestionBank from '../QuestionBank/AgenticQuestionBank';
import Modal from '../../../atoms/Modal/Modal';
import { AgenticLandingContentProps, DEFAULT_QUICK_ACTIONS, QuickAction, AgenticQuestion } from '../AskAsperAgentic.types';
import { transformQuestionBankCategories } from '../utils/transformQuestionBankCategories';

// Dummy custom questions for demonstration
const DEFAULT_SAVED_QUESTIONS: AgenticQuestion[] = [
    { id: 'custom1', text: 'What is the base vs incremental sales split for top brands in the category in last 52 weeks?' },
    { id: 'custom2', text: 'How does my brand pricing compare to competitors across different retailers?' },
    { id: 'custom3', text: 'Which products are gaining market share in the last quarter?' },
    { id: 'custom4', text: 'What is the price index trend over time for my portfolio?' },
];

/**
 * AgenticLandingContent - Center content area with welcome header, search input, and quick actions
 * Part of the AskAsper Agentic Platform components
 */
const AgenticLandingContent: React.FC<AgenticLandingContentProps> = ({
    userName = 'User',
    greeting,
    subtext = 'What can I help you with today ?',
    quickActions = DEFAULT_QUICK_ACTIONS,
    searchValue,
    onSearchChange,
    onSearchSubmit,
    onQuickActionClick,
    onQuestionBankClick,
    onSaveAsCustom,
    onQuestionSelect,
    onSearchInputFocus,
    onQuestionBankSearch,
    onQuestionBankSearchClear,
    onQuestionBankSectionClick,
    questionBankCategories,
    customQuestions,
    onEditCustomQuestion,
    onDeleteCustomQuestion,
    isLoading = false,
    showSidebarToggleInPanel = false,
    onSidebarToggleInPanel,
    lookupItems,
    selectedLookups,
    onLookupsChange,
    onLoadLookups,
    lookupCategories,
    onTrackEvent,
    analyticsThreadId,
    analyticsSessionId,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();
    const [isQuestionBankOpen, setIsQuestionBankOpen] = useState(false);
    const [savedQuestions, setSavedQuestions] = useState<AgenticQuestion[]>(DEFAULT_SAVED_QUESTIONS);

    const handleQuickActionClick = (action: QuickAction) => {
        // If action has a fullQuestion (prompt template), only populate the input bar - do not trigger submission
        if (action.fullQuestion && onSearchChange) {
            onSearchChange(action.fullQuestion);
            return;
        }

        // For actions without fullQuestion, call the custom handler if provided
        if (onQuickActionClick) {
            onQuickActionClick(action);
        }
    };

    const handleQuestionBankClick = () => {
        setIsQuestionBankOpen(true);
        // Call the external handler if provided
        if (onQuestionBankClick) {
            onQuestionBankClick();
        }
    };

    const handleQuestionSelect = (question: AgenticQuestion, categoryId: string) => {
        // Notify parent (used for analytics, e.g. distinguishing saved-question clicks)
        onQuestionSelect?.(question, categoryId);
        // Populate the search input with the selected question
        if (onSearchChange) {
            onSearchChange(question.text);
        }
        // Close the modal
        setIsQuestionBankOpen(false);
    };

    const handleEditSavedQuestion = (question: {
        id?: string | number;
        text?: string;
        QuestionID?: number;
        Question?: string;
    }) => {
        if (onEditCustomQuestion) {
            onEditCustomQuestion(question);
        } else {
            const questionId = question.id || question.QuestionID;
            const newText = question.text || question.Question || '';
            setSavedQuestions((prev) =>
                prev.map((q) => (String(q.id) === String(questionId) ? { ...q, text: newText } : q)),
            );
        }
    };

    const handleDeleteSavedQuestion = (questionId: string) => {
        if (onDeleteCustomQuestion) {
            onDeleteCustomQuestion(questionId);
        } else {
            setSavedQuestions((prev) => prev.filter((q) => q.id !== questionId));
        }
    };

    const handleSaveAsCustom = (value: string) => {
        // When customQuestions is provided (controlled mode), parent manages the list; only call the handler
        if (!customQuestions) {
            const newQuestion: AgenticQuestion = {
                id: `custom-${Date.now()}`,
                text: value,
            };
            setSavedQuestions((prev) => [...prev, newQuestion]);
        }
        if (onSaveAsCustom) {
            onSaveAsCustom(value);
        }
    };

    // Use customQuestions from parent when provided, else internal savedQuestions
    const effectiveSavedQuestions = customQuestions ?? savedQuestions;

    return (
        <ConfigProvider theme={theme}>
            <div
                className="flex flex-col gap-8 items-center relative w-full h-full justify-center"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-agentic-landing-content
            >
                {/* Sidebar toggle - when navbar is disabled, shown top-left of content */}
                {showSidebarToggleInPanel && onSidebarToggleInPanel && (
                    <div className="absolute top-0 left-0 z-10" data-test-id={`${dataTestId}-sidebar-toggle`}>
                        <Tooltip title="View all threads" placement="top" getPopupContainer={() => document.body}>
                            <button
                                type="button"
                                onClick={onSidebarToggleInPanel}
                                className="flex items-center justify-center w-8 h-8 hover:bg-[rgba(0,0,0,0.04)] rounded transition-colors"
                                aria-label="View all threads"
                            >
                                <Menu size={20} color="rgba(0,0,0,0.65)" />
                            </button>
                        </Tooltip>
                    </div>
                )}
                {/* Welcome Header */}
                <AgenticWelcomeHeader
                    userName={userName}
                    greeting={greeting}
                    subtext={subtext}
                    showLogo={true}
                    data-test-id={`${dataTestId}-welcome-header`}
                />

                {/* Search Input and Quick Actions Container */}
                <div className="flex flex-col gap-9 items-center w-full">
                    {/* Search Input */}
                    <AgenticSearchInput
                        value={searchValue}
                        onChange={onSearchChange}
                        onSubmit={onSearchSubmit}
                        onQuestionBankClick={handleQuestionBankClick}
                        onSaveAsCustom={handleSaveAsCustom}
                        onFocus={onSearchInputFocus}
                        isLoading={isLoading}
                        lookupItems={lookupItems}
                        selectedLookups={selectedLookups}
                        onLookupsChange={onLookupsChange}
                        onLoadLookups={onLoadLookups}
                        lookupCategories={lookupCategories}
                        onTrackEvent={onTrackEvent}
                        analyticsThreadId={analyticsThreadId}
                        analyticsSessionId={analyticsSessionId}
                        data-test-id={`${dataTestId}-search-input`}
                    />

                    {/* Quick Actions */}
                    <AgenticQuickActions
                        actions={quickActions}
                        onActionClick={handleQuickActionClick}
                        data-test-id={`${dataTestId}-quick-actions`}
                    />
                </div>

                {/* Question Bank Modal */}
                <Modal
                    open={isQuestionBankOpen}
                    onCancel={() => setIsQuestionBankOpen(false)}
                    footer={null}
                    width={1600}
                    centered
                    closable={false}
                    maskClosable={true}
                    className="question-bank-modal w-[80%]"
                    data-test-id={`${dataTestId}-question-bank-modal`}
                >
                    <div className="h-[80vh]">
                        <AgenticQuestionBank
                            categories={
                                questionBankCategories?.length
                                    ? transformQuestionBankCategories(questionBankCategories)
                                    : undefined
                            }
                            savedQuestions={effectiveSavedQuestions}
                            onClose={() => setIsQuestionBankOpen(false)}
                            onQuestionSelect={handleQuestionSelect}
                            onSectionClick={onQuestionBankSectionClick}
                            onEditSavedQuestion={handleEditSavedQuestion}
                            onDeleteSavedQuestion={handleDeleteSavedQuestion}
                            onSearch={onQuestionBankSearch}
                            onSearchClear={onQuestionBankSearchClear}
                            data-test-id={`${dataTestId}-question-bank`}
                        />
                    </div>
                </Modal>
            </div>
        </ConfigProvider>
    );
};

export default AgenticLandingContent;
